import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { tokenStore } from "@/lib/token";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bot, 
  Plus, 
  Zap,
  User,
  AlertCircle,
  Settings,
  Radio,
  RefreshCw,
  CreditCard,
  Check,
  AlertTriangle,
  Copy,
  Shield,
  Trash2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import UserAvatar from "@/components/UserAvatar";
import ProductTour from "@/components/ProductTour";
import { InvoicesSection } from "@/components/DashboardExtras";
import ProductCard from "@/components/ProductCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Bot type configurations for display
const BOT_DISPLAY = {
  gestion: {
    logo: "/favicon.png",
    colorClass: "text-blue-500",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/30",
    btnClass: "bg-blue-500 hover:bg-blue-600",
    icon: Settings,
    priceOffset: 0
  },
  vantaguard: {
    logo: "/images/vantaguard/logo.jpg",
    colorClass: "text-red-400",
    bgClass: "bg-red-500/10",
    borderClass: "border-red-500/30",
    btnClass: "bg-red-600 hover:bg-red-500",
    icon: Shield,
    priceOffset: 0
  },
  radio: {
    logo: "/favicon.png",
    colorClass: "text-green-500",
    bgClass: "bg-green-500/10",
    borderClass: "border-green-500/30",
    btnClass: "bg-green-500 hover:bg-green-600",
    icon: Radio,
    priceOffset: -0.50
  }
};

// Pricing plans
const BASE_PRICING = [
  { id: "basique", name: "Basique", basePrice: 2, duration: "1 mois", badge: "🥉" },
  { id: "standard", name: "Standard", basePrice: 4, duration: "2 mois", badge: "🥈", savings: "10%" },
  { id: "premium", name: "Premium", basePrice: 8, duration: "4 mois", badge: "🥇", savings: "20%" },
  { id: "gold", name: "Gold", basePrice: 16, duration: "8 mois", badge: "💎", savings: "33%" },
  { id: "platinum", name: "Platinum", basePrice: 24, duration: "12 mois", badge: "👑", savings: "50%" },
  { id: "lifetime", name: "Lifetime", basePrice: 46, duration: "À vie", badge: "♾️", savings: "Meilleur investissement" },
];

export default function Dashboard() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteBot, setDeleteBot] = useState(null);
  const [renewBot, setRenewBot] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [renewLoading, setRenewLoading] = useState(false);
  const [releasingId, setReleasingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [referral, setReferral] = useState(null);
  const [ambassador, setAmbassador] = useState(null);
  const [shopProducts, setShopProducts] = useState([]);
  const [renewWaiver, setRenewWaiver] = useState(false);
  const [sharedGuards, setSharedGuards] = useState([]);
  const [sharedBots, setSharedBots] = useState([]);
  const { user, logout, profile } = useAuth();
  const navigate = useNavigate();

  // Nouvelle clé : l'ancienne ne marche plus, la nouvelle s'active sur le prochain serveur.
  const regenerateLicense = async (botId) => {
    setReleasingId(botId);
    try {
      const token = tokenStore.get();
      await axios.post(`${API}/bots/${botId}/license/regenerate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Nouvelle clé générée : mets-la dans la config de ton serveur");
      await fetchBots();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de régénérer la clé");
    } finally {
      setReleasingId(null);
    }
  };

  // Téléchargement de l'acheteur : le zip est marqué à son empreinte (filigrane
  // invisible). On passe par une requête authentifiée puis on déclenche
  // l'enregistrement du fichier côté navigateur.
  const downloadProduct = async (bot) => {
    setDownloadingId(bot.id);
    try {
      const token = tokenStore.get();
      const res = await axios.get(`${API}/bots/${bot.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      // Produit à lien externe : le backend renvoie du JSON, pas un fichier.
      if (res.data && res.data.type === "application/json") {
        const text = JSON.parse(await res.data.text());
        if (text.external_url) { window.open(text.external_url, "_blank", "noopener"); return; }
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = bot.download_filename || "vantaphone.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let detail = "Téléchargement impossible";
      try {
        if (err.response?.data) detail = JSON.parse(await err.response.data.text()).detail || detail;
      } catch { /* blob non JSON */ }
      toast.error(detail);
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    const fetchReferral = async () => {
      try {
        const token = tokenStore.get();
        const res = await axios.get(`${API}/referral/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReferral(res.data);
        const amb = await axios.get(`${API}/ambassador/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (amb.data.is_ambassador) setAmbassador(amb.data);
      } catch {
        // silencieux : le widget de parrainage n'est pas critique
      }
    };
    fetchReferral();
  }, []);

  const fetchBots = useCallback(async () => {
    try {
      const token = tokenStore.get();
      const res = await axios.get(`${API}/bots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBots(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement de vos produits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBots();
    axios.get(`${API}/shop/bots`).then(res => setShopProducts(res.data || [])).catch(() => {});
    // Anti-cheats VantaGuard dont ce compte fait partie de l'équipe.
    axios.get(`${API}/ac/shared`, { headers: { Authorization: `Bearer ${tokenStore.get()}` } })
      .then(res => setSharedGuards(res.data?.items || [])).catch(() => {});
    // Bots de gestion dont on est buyer ou owner (sans les avoir achetés).
    axios.get(`${API}/dash/bots`, { headers: { Authorization: `Bearer ${tokenStore.get()}` } })
      .then(res => setSharedBots((res.data?.bots || []).filter(b => b.role !== 'acheteur'))).catch(() => {});
  }, [fetchBots]);

  // Formules de renouvellement : celles du produit en boutique, sinon les formules historiques.
  const shopPlansFor = (bot) => (shopProducts.find(p => p.id === bot?.bot_type)?.plans || []);
  const openRenew = (bot) => {
    const plans = shopPlansFor(bot);
    setRenewBot(bot);
    setRenewWaiver(false);
    setSelectedPlan(plans.length ? (plans.find(p => p.id === bot.plan_id)?.id || plans[0].id) : "premium");
  };

  const handleDeleteBot = async () => {
    if (!deleteBot) return;
    
    try {
      const token = tokenStore.get();
      await axios.delete(`${API}/bots/${deleteBot.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Produit supprimé");
      setBots(bots.filter(b => b.id !== deleteBot.id));
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteBot(null);
    }
  };

  const handleRenewBot = async () => {
    if (!renewBot) return;
    
    setRenewLoading(true);
    
    try {
      const token = tokenStore.get();
      const response = await axios.post(`${API}/checkout/renew`, {
        bot_id: renewBot.id,
        plan_id: selectedPlan,
        origin_url: window.location.origin,
        withdrawal_waiver: !!renewBot.downloadable && renewWaiver
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du renouvellement");
      setRenewLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Suppression automatique côté serveur : 60 jours après l'expiration (GRACE_PERIOD_DAYS).
  const autoDeleteAt = (bot) => new Date(new Date(bot.expires_at).getTime() + 60 * 86400000).toISOString();

  const isExpired = (bot) => {
    if (!bot.expires_at) return false;
    return new Date(bot.expires_at) < new Date();
  };

  // Un produit remplacé (passage au pack, essai suivi d'un achat) n'est plus affiché.
  const visibleBots = bots.filter(bot => !['upgraded', 'converted'].includes(bot.status));
  const expiredBots = visibleBots.filter(bot => isExpired(bot) || bot.status !== 'active');
  const activeBots = visibleBots.filter(bot => !isExpired(bot) && bot.status === 'active');

  // Get pricing for selected bot
  const getRenewPricing = () => {
    if (!renewBot) return BASE_PRICING;
    const shopPlans = shopPlansFor(renewBot);
    if (shopPlans.length) {
      return shopPlans.map(plan => ({ ...plan, badge: "", duration: plan.duration_label || `${plan.duration_days} jours` }));
    }
    const botDisplay = BOT_DISPLAY[renewBot.bot_type] || BOT_DISPLAY.gestion;
    return BASE_PRICING.map(plan => ({
      ...plan,
      price: plan.basePrice + (botDisplay.priceOffset || 0)
    }));
  };

  const renewPricing = getRenewPricing();
  const currentPlan = renewPricing.find(p => p.id === selectedPlan);
  const renewBotDisplay = renewBot ? (BOT_DISPLAY[renewBot.bot_type] || BOT_DISPLAY.gestion) : null;

  return (
    <div className="min-h-screen bg-vanta-base">
      <Navbar />
      
      <main className="relative pt-24 pb-16">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-80 overflow-hidden">
          <div className="absolute left-1/3 top-[-180px] h-[360px] w-[820px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/20 via-violet-600/15 to-transparent blur-[90px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="page-rise relative overflow-hidden flex flex-col sm:flex-row justify-between items-start sm:items-center gap-5 mb-6 rounded-3xl border border-white/[0.07] bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-6 sm:p-7">
            <div className="flex items-center gap-4 min-w-0">
              <UserAvatar profile={profile || user} size={56} className="shrink-0 ring-2 ring-white/10" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.14em] text-white/35">Espace client</p>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight truncate">
                  Bonjour, {user?.username}
                </h1>
                <p className="text-sm text-white/45 mt-0.5">Gère tes produits, tes licences et tes factures.</p>
              </div>
            </div>
            
            <Button 
              data-testid="add-bot-btn"
              onClick={() => navigate('/shop')}
              className="h-11 bg-gradient-to-r from-blue-600 to-violet-600 hover:brightness-110 text-white px-5 rounded-xl font-semibold shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)] shrink-0"
            >
              <Plus className="w-5 h-5 mr-2" />
              Acheter un produit
            </Button>
          </div>

          {/* Stats */}
          <div data-testid="dashboard-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              ["Produits actifs", activeBots.length, "text-blue-400", Zap],
              ["Produits expirés", expiredBots.length, expiredBots.length ? "text-red-400" : "text-white/60", AlertCircle],
              ["Total produits", visibleBots.length, "text-white", Bot],
            ].map(([label, value, color, Icon], index) => (
              <div key={label} className="page-rise bg-[#0f1019] border border-white/[0.07] rounded-2xl p-4 sm:p-5" style={{ animationDelay: `${60 + index * 40}ms` }}>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-white/45">{label}</p>
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/[0.05] ring-1 ring-inset ring-white/[0.06]"><Icon className={`w-4 h-4 ${color}`} /></span>
                </div>
                <p className={`font-heading text-3xl font-bold mt-1 ${color}`}>{value}</p>
              </div>
            ))}
            <div className="page-rise bg-[#0f1019] border border-white/[0.07] rounded-2xl p-4 sm:p-5 min-w-0" style={{ animationDelay: "180ms" }}>
              <div className="flex items-center justify-between">
                <p className="text-xs text-white/45">Compte</p>
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/[0.05] ring-1 ring-inset ring-white/[0.06]"><User className="w-4 h-4 text-white/70" /></span>
              </div>
              <p className="font-semibold mt-2 truncate" title={user?.email}>{user?.email}</p>
              <Link to="/settings?tab=billing" className="text-xs text-blue-400 hover:text-blue-300 mt-1 inline-block">Factures et abonnements</Link>
            </div>
          </div>

          {/* Referral widget */}
          {referral && (
            <div data-testid="referral-widget" className="page-rise bg-gradient-to-r from-blue-600/[0.12] to-violet-600/[0.12] border border-white/[0.07] rounded-2xl p-5 mb-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold mb-1">{ambassador ? "Ambassadeur Vanta Pulse" : "Programme de parrainage"}</p>
                <p className="text-text-secondary text-sm">
                  {ambassador
                    ? `${ambassador.stats.signups} inscription(s) · ${ambassador.stats.sales} vente(s) · ${ambassador.stats.pending.toFixed(2)} € de commission à recevoir`
                    : referral.total_referred > 0
                    ? `${referral.total_referred} filleul(s) · ${referral.total_rewards} récompense(s) obtenue(s)`
                    : "Parraine un ami : dès son premier achat, tu reçois un code promo -20%."}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Link to={ambassador ? "/ambassadeur" : "/recrutement"} className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-violet-600 hover:brightness-110 text-white px-4 py-2.5 rounded-xl text-sm" data-testid="ambassador-entry">
                  {ambassador ? "Espace ambassadeur" : "Devenir ambassadeur"}
                </Link>
                <button
                  onClick={() => { navigator.clipboard.writeText(ambassador?.link || referral.referral_link); toast.success("Lien de parrainage copié"); }}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-4 py-2.5 rounded-xl text-sm"
                >
                  <Copy className="w-4 h-4" />
                  Copier mon lien
                </button>
              </div>
            </div>
          )}

          {/* Expired Bots Section - Renewal */}
          {expiredBots.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="w-6 h-6 text-status-warning" />
                <h2 className="font-heading text-xl font-semibold">Produits à renouveler</h2>
              </div>
              
              <div className="bg-[#0f1019] border border-amber-500/20 rounded-2xl p-5 sm:p-6">
                <p className="text-text-secondary mb-6">
                  Ces produits ont expiré et doivent être renouvelés pour continuer à fonctionner.
                  Sans renouvellement, ils sont supprimés automatiquement 2 mois après l'expiration.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {expiredBots.map((bot) => {
                    const botDisplay = BOT_DISPLAY[bot.bot_type] || BOT_DISPLAY.gestion;
                    
                    return (
                      <div 
                        key={bot.id}
                        className="bg-black/20 border border-red-500/20 rounded-xl p-4 flex flex-col gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <img 
                            src={bot.bot_avatar_url || botDisplay.logo} 
                            alt={bot.bot_type_name} 
                            className="w-10 h-10 rounded-full object-cover opacity-50 shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="font-semibold text-white">{bot.bot_name}</p>
                            <p className="text-xs text-status-error">{bot.is_trial ? 'Essai terminé le' : 'Expiré le'} {formatDate(bot.expires_at)}</p>
                            {bot.expires_at && <p className="text-[11px] text-white/40" data-testid={`auto-delete-${bot.id}`}>Suppression auto : {new Date(autoDeleteAt(bot)).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 [&>button:first-child]:flex-1">
                        
                        {bot.is_trial ? (
                          <Button
                            data-testid={`buy-after-trial-${bot.id}`}
                            onClick={() => navigate(`/bot/${bot.bot_type}`)}
                            className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 rounded-lg text-sm font-semibold shrink-0"
                          >
                            <CreditCard className="w-4 h-4 mr-1" />
                            Acheter
                          </Button>
                        ) : (
                          <Button
                            data-testid={`renew-expired-${bot.id}`}
                            onClick={() => openRenew(bot)}
                            className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 rounded-lg text-sm font-semibold shrink-0"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Renouveler
                          </Button>
                        )}
                          <button data-testid={`delete-bot-${bot.id}`} onClick={() => setDeleteBot(bot)} title="Supprimer maintenant"
                            className="p-2 rounded-lg text-white/30 hover:text-red-300 hover:bg-red-500/10 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Active Bots List */}
          <div data-testid="active-bots-section" className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-5 flex items-center gap-2.5">Vos produits actifs{activeBots.length > 0 && <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-xs font-medium text-white/50">{activeBots.length}</span>}</h2>
            
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-6 animate-pulse">
                    <div className="h-6 bg-vanta-elevated rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-vanta-elevated rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-vanta-elevated rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : activeBots.length === 0 ? (
              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-12 text-center">
                <Bot className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">Aucun produit actif</h3>
                <p className="text-text-secondary mb-6">
                  {expiredBots.length > 0 
                    ? "Renouvelez vos produits expirés ou découvrez-en un nouveau !"
                    : "Vous n'avez pas encore de produit. Faites un tour dans la boutique !"
                  }
                </p>
                <Button 
                  data-testid="empty-add-bot-btn"
                  onClick={() => navigate('/shop')}
                  className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Acheter un produit
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
                {activeBots.map((bot) => (
                  <ProductCard
                    key={bot.id}
                    bot={bot}
                    display={BOT_DISPLAY[bot.bot_type] || BOT_DISPLAY.gestion}
                    products={shopProducts}
                    navigate={navigate}
                    onDownload={downloadProduct}
                    downloading={downloadingId === bot.id}
                    onRegenerate={regenerateLicense}
                    regenerating={releasingId === bot.id}
                    onRefresh={fetchBots}
                  />
                ))}
              </div>
            )}
          </div>

          {sharedBots.length > 0 && (
            <div className="mb-12" data-testid="shared-bots">
              <h2 className="font-heading text-xl font-bold mb-1">Bots partagés avec vous</h2>
              <p className="text-sm text-text-muted mb-4">Vous êtes buyer ou owner de ces bots.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sharedBots.map((b) => (
                  <Link key={b.id} to={`/bot-dashboard/${b.id}`} className="flex items-center gap-3 bg-[#0f1019] border border-white/[0.07] rounded-2xl hover:border-violet-500/40 p-4" data-testid={`shared-bot-${b.id}`}>
                    {b.bot_avatar_url ? <img src={b.bot_avatar_url} alt="" className="w-10 h-10 rounded-full shrink-0" /> : <div className="w-10 h-10 bg-violet-500/10 border border-violet-500/30 flex items-center justify-center shrink-0"><Bot className="w-5 h-5 text-violet-300" /></div>}
                    <div className="min-w-0">
                      <div className="font-medium truncate">{b.bot_name}</div>
                      <div className="text-xs text-text-muted">{b.role_label} · {b.guild_count} serveur{b.guild_count > 1 ? "s" : ""}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {sharedGuards.length > 0 && (
            <div className="mb-12" data-testid="shared-anticheats">
              <h2 className="font-heading text-xl font-bold mb-1">Anti-cheats partagés avec vous</h2>
              <p className="text-sm text-text-muted mb-4">Vous faites partie du staff de ces serveurs.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sharedGuards.map((g) => (
                  <Link key={g.bot_id} to={`/anticheat/${g.bot_id}`} className="flex items-center gap-3 bg-[#0f1019] border border-white/[0.07] rounded-2xl hover:border-red-500/40 p-4" data-testid={`shared-anticheat-${g.bot_id}`}>
                    <div className="w-10 h-10 bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0"><Shield className="w-5 h-5 text-red-400" /></div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{g.server_name}</div>
                      <div className="text-xs text-text-muted">VantaGuard · {g.role_label}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <InvoicesSection />
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteBot} onOpenChange={() => setDeleteBot(null)}>
        <AlertDialogContent className="bg-vanta-surface border-border-default">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Supprimer ce produit ?</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              Supprimer <span className="text-white">{deleteBot?.bot_name}</span> maintenant ? Il ne pourra plus être renouvelé.
              Cette action est irréversible (tes factures restent disponibles).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border-default hover:bg-vanta-base rounded-lg">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              data-testid="confirm-delete-btn"
              onClick={handleDeleteBot}
              className="bg-status-error hover:bg-status-error/80 rounded-lg"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Renewal Modal */}
      <Dialog open={!!renewBot} onOpenChange={() => setRenewBot(null)}>
        <DialogContent className="bg-vanta-surface border-border-default sm:max-w-lg p-0 overflow-hidden">
          <div className="p-6">
            <DialogHeader className="mb-6">
              <DialogTitle className="font-heading text-2xl font-bold tracking-tighter flex items-center gap-3">
                <RefreshCw className={`w-6 h-6 ${renewBotDisplay?.colorClass || 'text-blue-500'}`} />
                Renouveler l'abonnement
              </DialogTitle>
            </DialogHeader>

            {renewBot && (
              <div className="space-y-6">
                {/* Bot Info */}
                <div className={`bg-vanta-base border ${renewBotDisplay?.borderClass || 'border-border-default'} p-4 flex items-center gap-4`}>
                  <img 
                    src={renewBot.bot_avatar_url || renewBotDisplay?.logo} 
                    alt={renewBot.bot_type_name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-heading font-bold text-lg">{renewBot.bot_name}</p>
                    <p className={`text-sm ${renewBotDisplay?.colorClass || 'text-blue-500'}`}>
                      {renewBot.bot_type_name}
                    </p>
                    <p className="text-xs text-status-error mt-1">
                      {isExpired(renewBot) ? `Expiré le ${formatDate(renewBot.expires_at)}` : `Expire le ${formatDate(renewBot.expires_at)}`}
                    </p>
                  </div>
                </div>

                {/* Plan Selection Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Choisir la durée de renouvellement
                  </label>
                  <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                    <SelectTrigger 
                      data-testid="renew-plan-select"
                      className="bg-vanta-base border-border-default focus:border-blue-500 rounded-none h-14 text-lg relative"
                    >
                      <SelectValue className="pr-8" />
                    </SelectTrigger>
                    <SelectContent className="bg-vanta-surface border-border-default">
                      {renewPricing.map((plan) => (
                        <SelectItem 
                          key={plan.id} 
                          value={plan.id}
                          className="focus:bg-vanta-base py-3"
                        >
                          <span className="flex items-center gap-3 text-base">
                            <span className="text-xl">{plan.badge}</span>
                            <span className="font-bold">{plan.price.toFixed(2)}€</span>
                            <span className="text-text-muted">-</span>
                            <span>{plan.duration}</span>
                            {plan.savings && (
                              <span className={`text-sm ${renewBotDisplay?.colorClass || 'text-blue-500'}`}>
                                ({plan.savings})
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Selected Plan Summary */}
                {currentPlan && (
                  <div className={`bg-vanta-base border ${renewBotDisplay?.borderClass || 'border-border-default'} p-4`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{currentPlan.badge}</span>
                        <span className="font-heading font-bold">{currentPlan.name}</span>
                      </div>
                      <div className="text-right">
                        <p className={`font-heading text-2xl font-bold ${renewBotDisplay?.colorClass || 'text-blue-500'}`}>
                          {currentPlan.price.toFixed(2)}€
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {["Activation immédiate", "Support 24/7"].map((f) => (
                        <div key={f} className="flex items-center gap-2 text-text-secondary">
                          <Check className={`w-3 h-3 ${renewBotDisplay?.colorClass || 'text-blue-500'}`} />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {renewBot.downloadable && (
                  <label className="flex items-start gap-3 text-sm text-text-secondary cursor-pointer">
                    <input data-testid="renew-waiver" type="checkbox" checked={renewWaiver} onChange={(e) => setRenewWaiver(e.target.checked)} className="mt-1 accent-blue-500" />
                    <span>Je demande l'accès immédiat et je reconnais perdre mon droit de rétractation (<Link to="/cgv" target="_blank" className="text-blue-400 underline">CGV</Link>).</span>
                  </label>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setRenewBot(null)}
                    className="flex-1 border-border-default hover:bg-vanta-base rounded-none py-5"
                  >
                    Annuler
                  </Button>
                  <Button
                    data-testid="confirm-renew-btn"
                    onClick={handleRenewBot}
                    disabled={renewLoading || (renewBot.downloadable && !renewWaiver)}
                    className={`flex-1 ${renewBotDisplay?.btnClass || 'bg-blue-500 hover:bg-blue-600'} text-white rounded-none py-5 font-bold`}
                  >
                    {renewLoading ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Payer {currentPlan?.price.toFixed(2)}€
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {!loading && (
        <ProductTour
          storageKey="vantapulse_tour_dashboard_v1"
          steps={[
            {
              target: '[data-testid="add-bot-btn"]',
              title: "Achetez votre premier produit",
              body: "C'est ici que vous choisissez un produit dans la boutique et sa formule.",
            },
            {
              target: '[data-testid="dashboard-stats"]',
              title: "Vue d'ensemble",
              body: "Suivez en un coup d'œil vos produits actifs, expirés, et les infos de votre compte.",
            },
            {
              target: '[data-testid="active-bots-section"]',
              title: "Vos produits",
              body: "Retrouvez ici tous vos produits actifs : téléchargement, licence, gestion et renouvellement.",
            },
          ]}
        />
      )}
    </div>
  );
}
